import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, useTheme, Alert } from '@mui/material';
import React from 'react';
import { CerebroDrawer, CerebroHeading, CerebroLinkButton, CerebroLink, CerebroText } from '../../shared/components';
import { useDownloadRequestQuery } from '../../shared/hooks';
import { CurrentUser } from '../../shared/models';
import { DateUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';

interface DownloadRequestsProps {
	isReady: boolean;
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
}

export const DownloadRequests: React.FC<DownloadRequestsProps> = ({ isReady, dataRoomGuid, currentUser }) => {
	const theme = useTheme();
	const requestsQuery = useDownloadRequestQuery(dataRoomGuid, currentUser?.userGuid, {
		enabled: currentUser != null && isReady,
		retry: true,
		retryDelay: 5000,
	});
	const [seeDownloads, setSeeDownloads] = React.useState<boolean>(false);

	if (requestsQuery.isLoading || !requestsQuery.isSuccess) return <></>;

	const requests = requestsQuery.data;
	if (requests.length == 0) return <></>;

	const hasReady = requests.filter(r => r.isReady).length > 0;
	const hasPending = requests.filter(r => !r.isReady).length > 0;
	const hasBoth = hasReady && hasPending;
	const alertMessage = hasBoth
		? 'Download(s) are being processed, other download(s) are ready.'
		: hasReady
		? 'Your download(s) are ready. '
		: 'Download(s) are being processed... ';

	return (
		<>
			<Alert
				severity="info"
				sx={{
					marginBottom: 3,
					boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
					border: 'none',
					backgroundColor: 'white',
					fontWeight: 'bold',
				}}
			>
				{alertMessage}{' '}
				{hasReady && (
					<>
						<CerebroLink onClick={() => setSeeDownloads(true)} sx={{ cursor: 'pointer' }}>
							Click here
						</CerebroLink>{' '}
						<span>to see downloads.</span>
					</>
				)}
			</Alert>

			<CerebroDrawer
				open={seeDownloads}
				onClose={() => setSeeDownloads(false)}
				PaperProps={{ sx: { width: '600px' } }}
			>
				<CerebroHeading sx={{ marginBottom: 2 }}>Download Requests</CerebroHeading>

				<TableContainer sx={{ marginBottom: 2 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Request</TableCell>
								<TableCell>Request Date</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{requests.map(item => (
								<TableRow key={item.guid}>
									<TableCell>{item.requestDescription}</TableCell>
									<TableCell>
										{DateUtils.FormatStringAsShortDate(item.createdOn.toString())}
									</TableCell>
									<TableCell>
										{item.isReady ? (
											<CerebroLinkButton
												href={CerebroApiEndpoints.Documents.AbsoluteDownloadRequest(
													dataRoomGuid,
													currentUser.userGuid,
													item.guid
												)}
												target="_blank"
												rel="noreferrer"
												variant="link"
											>
												Download
											</CerebroLinkButton>
										) : (
											<CerebroText>Processing</CerebroText>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</CerebroDrawer>
		</>
	);
};
